import { moneyCurrency } from './money';
import { CLIENT_CURRENCIES, type CurrencyCode } from './country/pack.generated';

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* The name is historical and the behaviour is not: this prints the
   SIGNED-IN HOSPITAL's currency, which on a Kenyan hospital is the literal
   this function has always produced, character for character, and on a
   Ugandan one is whole shillings marked UGX.

   It was left hard-wired when the country round moved the rest of the
   estate onto the pack, so forty-one hospital-facing calls — the Meridian
   billing screen, the overdue banner, the refunds panel, the pharmacy's
   decline dialog and the patient banner — printed KES to a Kampala
   administrator looking at a bill denominated in shillings. One
   delegation answers all of them.

   Kenya's output is unchanged by construction: its symbol is KES, its
   locale en-KE, and with two minor units the digit options are omitted
   exactly as before, so `toLocaleString` is called the way it always was.
   The money goldens in `pnpm country:check` hold that claim. */
export function formatKES(amount: number): string {
  const currency = moneyCurrency();
  return `${currency.symbol} ${amount.toLocaleString(
    currency.locale,
    currency.minorUnits === 0 ? { maximumFractionDigits: 0 } : undefined,
  )}`;
}

/* The same sentence for a currency NAMED rather than ambient.
 *
 * `formatKES` above reads the signed-in hospital's currency, which is right
 * in the hospital app and useless in the Meridian Console: an operator is
 * not signed into a hospital, so the ambient currency stays at its KES
 * default and every figure on every hospital's page printed KES whatever
 * that hospital is billed in. A Ugandan hospital's Activate-billing dialog
 * offered "25000 KES" and promised "KES 25,000 in total" for an account
 * denominated in shillings.
 *
 * The Console cannot have one ambient currency, because it looks at many
 * hospitals at once and they do not share one. So it names the currency at
 * every call instead, from the tenant on screen.
 *
 * The expression below is `formatKES`'s, character for character, with the
 * currency passed in rather than read: `formatMoneyIn('KES', n)` and
 * `formatKES(n)` on a Kenyan session are the same string by construction. */
export function formatMoneyIn(code: CurrencyCode | null | undefined, amount: number): string {
  const currency = (code && CLIENT_CURRENCIES[code]) || moneyCurrency();
  return `${currency.symbol} ${amount.toLocaleString(
    currency.locale,
    currency.minorUnits === 0 ? { maximumFractionDigits: 0 } : undefined,
  )}`;
}

/* Waits read at every scale: minutes inside the hour, hours and minutes
   through the day, then days. A three- or five-figure minute count tells
   nobody anything (the staging tester's walk of #248 read "Open note,
   11249 min" on a doctor's home and "waiting 1953 min" on a floor): every
   screen that shows a wait or an age in minutes says it through here. */
export function formatWait(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  if (hours < 24) {
    const rest = total % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  if (days === 1) return restHours === 0 ? '1 day' : `1 day ${restHours} h`;
  return `${days} days`;
}
